/**
 * Why a login failed, as a code the UI translates — never the backend's own
 * text, which is English-only ("Invalid credentials", "Too many failed
 * attempts, try again later") and would show untranslated in es/pt.
 * Derived from the HTTP status the Auth login handler returns.
 */
export const LOGIN_ERROR_CODES = ['invalidCredentials', 'tooManyAttempts', 'unknown'] as const;

export type LoginErrorCode = (typeof LOGIN_ERROR_CODES)[number];

/** The context's `error` holds a code after a failed login, free text after a failed session restore. */
export function isLoginErrorCode(value: unknown): value is LoginErrorCode {
  return LOGIN_ERROR_CODES.includes(value as LoginErrorCode);
}

const HTTP_UNAUTHORIZED = 401;
const HTTP_TOO_MANY_REQUESTS = 429;

export function loginErrorCodeFor(status: number | undefined): LoginErrorCode {
  if (status === HTTP_UNAUTHORIZED) return 'invalidCredentials';
  if (status === HTTP_TOO_MANY_REQUESTS) return 'tooManyAttempts';
  return 'unknown';
}

/** Thrown by authService.login, carrying the code alongside the backend message. */
export class LoginError extends Error {
  readonly code: LoginErrorCode;

  constructor(message: string, code: LoginErrorCode) {
    super(message);
    this.name = 'LoginError';
    this.code = code;
  }
}

/** The code for any error a login attempt can throw (network errors → 'unknown'). */
export function loginErrorCodeOf(error: unknown): LoginErrorCode {
  return error instanceof LoginError ? error.code : 'unknown';
}
